import { action, computed, observable, runInAction, toJS } from 'mobx';
import {
    Accion,
    ConsultaUsuarios,
    Municipio,
    Usuario,
    UsuarioPassword,
    ConsultaUsuariosResponse
} from '../models';
import { FiltrosUsuarios } from '../models/Consultas';
import { encryptStore } from './encryptStore';
import { requestStore } from './requestStore';
import { tokenStore } from './tokenStore';
import { UsuarioAlta } from '../models/Altas';

export class UserStore {
    @observable
    isFetching: boolean = true;

    @observable
    isGettingActions: boolean = false;

    @observable
    username: string | null = tokenStore.username;

    @observable
    email: string | null = tokenStore.username;

    @observable
    rol: string = '';

    @observable
    municipio: Municipio[] = [];

    @observable
    acciones: Accion[] = [];

    @observable
    capturaAgua: boolean = false;

    @observable
    banderaPassword: boolean = false;

    @observable
    newPassword: string = '';

    @observable
    confirmPassword: string = '';

    @observable
    listaUsuarios: ConsultaUsuarios[] = [];

    @observable
    filtrosConsultaUsuarios: FiltrosUsuarios = {
        municipio: '',
        idRol: 0
    };

    @observable
    openModal: boolean = false;

    @observable
    usuarioAlta: UsuarioAlta = {
        id: 0,
        idCargo: '0',
        cargoNombre: '',
        contraseña: '',
        email: '',
        municipio: '0',
        nombre: '',
        idRol: '0',
        rolNombre: '',
        usuarioAcceso: ''
    };

    @observable
    tipoModal: 'Alta' | 'Consulta' | 'Editar' = 'Consulta';

    @observable
    idUsuariConsulta: number = 0;

    @observable
    tipoUsuario:string='';

    @computed
    get accioneGroups(): Record<string, string[]> {
        return this.acciones.reduce(
            (groups, accion) => {
                groups[accion.menu] = groups[accion.menu] || [];
                groups[accion.menu].push(accion.nombre);
                return groups;
            },
            {} as Record<string, string[]>
        );
    }

    @action
    async getUsuario() {
        this.isFetching = true;
        const usuarioResponse = await requestStore.getUsuario();
        if (usuarioResponse) {
            this.actualizarUsuario(usuarioResponse);
        }

        runInAction(() => (this.isFetching = false));
    }

    @action
    actualizarUsuario(usuarioResponse: Usuario) {
        this.municipio = usuarioResponse.municipios;
        this.username = usuarioResponse.nombre;
        this.email = usuarioResponse.email;
        this.rol = usuarioResponse.rol && usuarioResponse.rol.nombre;
        this.banderaPassword = usuarioResponse.banderaPassword;
        this.openModal=false;
        this.tipoUsuario=usuarioResponse!.tipo;
    }

    @action
    async actualizarContraseña() {
        const usuarioActualiza: string | null = sessionStorage.getItem('usuario');
        this.newPassword = encryptStore.encriptar(this.newPassword);
        const usuarioPassword: UsuarioPassword = {
            usuarioAcceso: usuarioActualiza,
            password: this.newPassword
        };
        const actualizaResponse = await requestStore.updatePassword(usuarioPassword);
        if (actualizaResponse) {
            sessionStorage.removeItem('pswd');
            sessionStorage.setItem('pswd', this.newPassword);
            tokenStore!.password = this.newPassword;
            tokenStore!.getToken();
            await this.getUsuario();
            this.banderaPassword = true;
        }
    }

    @action
    async getAcciones() {
        this.isGettingActions = true;
        const accionesResponse = await requestStore.getAcciones();
        if (accionesResponse) {
            this.acciones = accionesResponse;
        }
        runInAction(() => (this.isGettingActions = false));
    }

    @computed
    get estaAutorizado(): boolean {
        const path = decodeURI(window.location.pathname).toLocaleLowerCase();
        const validPaths = this.acciones.map(({ menu, nombre }) =>
            `/${menu}/${nombre}`.toLocaleLowerCase()
        );
        return validPaths.indexOf(path) > -1;
    }

    @action
    async setPermisoAgua(idMunicipio: number) {
        this.capturaAgua = this.municipio.find(mun => mun.id === idMunicipio)!.captura;
    }

    @action
    async limpiarUsuario() {
        this.acciones.length = 0;
    }

    @action
    async getUsuarios() {
        this.isFetching = true;
        const response = await requestStore!.getConsultaUsuarios(
            this.filtrosConsultaUsuarios!.municipio,
            this.filtrosConsultaUsuarios!.idRol
        );
        if (response) {
            this.listaUsuarios = response as ConsultaUsuariosResponse;
        }
        this.isFetching = false;
    }

    @action
    async getResponsiva(idUsuario: number) {
        const response = await requestStore.getResponsiva(idUsuario);
        return response;
    }

    @action
    async registrarUsuario() {
        this.usuarioAlta!.municipio =
            this.usuarioAlta!.municipio == '' ? '0' : this.usuarioAlta!.municipio;
        this.usuarioAlta.usuarioAcceso = encryptStore!.encriptar(this.usuarioAlta!.usuarioAcceso);
        this.usuarioAlta.contraseña = this.usuarioAlta!.usuarioAcceso;

        const response = await requestStore.postUsuario(
            parseInt(this.usuarioAlta.idRol),
            parseInt(this.usuarioAlta.idCargo),
            parseInt(this.usuarioAlta.municipio),
            this.usuarioAlta.nombre,
            this.usuarioAlta.email,
            this.usuarioAlta.usuarioAcceso,
            this.usuarioAlta.contraseña
        );
        if(response){
            this.limpiarUsuarioAlta();
        }
        this.openModal = false;
        return response;
    }

    @action
    async editarUsuario() {
        const response = await requestStore.postEditarUsuario(
            this.usuarioAlta.id,
            parseInt(this.usuarioAlta.idCargo),
            this.usuarioAlta.email);
        if(response)
        {
            this.getUsuarios();
        }
        this.openModal=false;
        return response;
    }

    @action
    async getUsuarioConsulta() {
        const response = await requestStore.getUsuarioConsulta(this.idUsuariConsulta);
        if (response) {
            this.usuarioAlta.id = parseInt(response.id);
            this.usuarioAlta.idCargo = response.cargo.id.toString();
            this.usuarioAlta.cargoNombre = response.cargo.nombre;
            this.usuarioAlta.contraseña = '';
            this.usuarioAlta.email = response.email;
            this.usuarioAlta.municipio = response.tipo;
            this.usuarioAlta.nombre = response.nombre;
            this.usuarioAlta.idRol = response!.rol!.id.toString();
            this.usuarioAlta.rolNombre = response!.rol!.nombre;
            this.usuarioAlta.usuarioAcceso = response.usuarioAcceso;
        }
        return response;
    }

    @action
    limpiarUsuarioAlta() {
        this.usuarioAlta.id = 0;
        this.usuarioAlta.idCargo = '0';
        this.usuarioAlta.cargoNombre = '';
        this.usuarioAlta.contraseña = '';
        this.usuarioAlta.email = '';
        this.usuarioAlta.municipio = '';
        this.usuarioAlta.nombre = '';
        this.usuarioAlta.idRol = '0';
        this.usuarioAlta.rolNombre = '';
        this.usuarioAlta.usuarioAcceso = '';
    }

    @action
    async restablecerPassword() {
        const response = await requestStore.putRestablecerPassword(this.usuarioAlta!.id);
        if(response)
        {
            this.getUsuarios();
        }
        this.openModal=false;
        return response;
    }

    @action
    async eliminarUsuario() {
        const response = await requestStore.postEliminarUsuario(this.usuarioAlta.id);
        if(response)
        {
            this.getUsuarios();
        }
        this.openModal=false;
        return response;
    }
}

export const userStore = new UserStore();
export default userStore;
